import { FormHelperText, Grid, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import {
  DEFAULT_ARCHITECTURE,
  DEFAULT_MEMORY,
  DEFAULT_RUNTIME,
  DEFAULT_SNAPSTART_ENABLED,
  DEFAULT_SNAP_ALIAS_ENABLED,
  DEFAULT_TIMEOUT,
} from 'config-editor-common';
import type { Lambda, LambdaDefaults } from 'config-editor-common';
import { CapacityProviderSelect } from './CapacityProviderSelect';
import { DefaultTextField } from './DefaultTextField';
import { LambdaEnvVarsEditor } from './LambdaEnvVarsEditor';
import { LambdaLayersSelector } from './LambdaLayersSelector';
import { MemoryTimeoutSnapstartFields } from './MemoryTimeoutSnapstartFields';
import { RuntimeArchitectureFields } from './RuntimeArchitectureFields';
import { SecurityGroupsSelector } from './SecurityGroupsSelector';

interface LambdaFieldsEditorProps {
  /**
   * The lambda or defaults object being edited
   */
  value: Lambda | LambdaDefaults;
  /**
   * Callback when any field changes
   */
  onChange: (updates: Partial<Lambda | LambdaDefaults>) => void;
  /**
   * Whether this is for authorizer lambdas (affects default snapstart)
   */
  isAuthorizer?: boolean;
  /**
   * Whether to show lambda_source field (defaults: true, individual lambdas: false as it's in basic fields)
   */
  showLambdaSource?: boolean;
  /**
   * Whether to show s3_key field (defaults: true, individual lambdas: false)
   */
  showS3Key?: boolean;
  /**
   * Whether to show the capacity_provider field for LMI (Lambda Managed Instances).
   * Only individual lambdas support LMI, not defaults.
   */
  showCapacityProvider?: boolean;
  /**
   * Whether to show the "call other lambdas" (lambda_integration_arns) input.
   * Only individual lambdas use it, not defaults.
   */
  showLambdaIntegrations?: boolean;
  /**
   * Template name (for env vars editor)
   */
  /**
   * Lambda name — threaded to the memory/timeout/snapstart fields so the AI copilot's coach-mark can
   * point at the exact field (data-ai-anchor). Omitted for the defaults editor.
   */
  anchorName?: string;
  /**
   * Custom labels for fields
   */
  labels?: {
    runtime?: string;
    architecture?: string;
    memory?: string;
    timeout?: string;
    envVars?: string;
  };
}

/**
 * Generic component for editing lambda fields (runtime, architecture, memory, timeout, snapstart, etc.)
 * Can be used for both LambdaDefaults and individual Lambda editing
 */
export const LambdaFieldsEditor = ({
  value,
  onChange,
  isAuthorizer = false,
  showLambdaSource = true,
  showS3Key = true,
  showCapacityProvider = false,
  showLambdaIntegrations = false,
  anchorName,
  labels = {},
}: LambdaFieldsEditorProps) => {
  const defaultSnapstart = isAuthorizer ? true : DEFAULT_SNAPSTART_ENABLED;

  // LMI (Lambda Managed Instances): when a capacity provider is set, snapstart, the
  // secrets layer, and VPC settings (security groups) are not supported.
  const lmiEnabled = showCapacityProvider && !!(value as Lambda).capacity_provider;
  const LMI_INCOMPATIBLE_REASON = 'Not supported for LMI lambdas (capacity provider set).';

  const handleFieldChange = <K extends keyof (Lambda | LambdaDefaults)>(
    field: K,
    fieldValue: (Lambda | LambdaDefaults)[K],
  ) => {
    onChange({ [field]: fieldValue } as Partial<Lambda | LambdaDefaults>);
  };

  return (
    <>
      <Grid container spacing={2}>
        <RuntimeArchitectureFields
          runtime={value.runtime ?? DEFAULT_RUNTIME}
          architecture={value.architecture ?? DEFAULT_ARCHITECTURE}
          onRuntimeChange={newValue => handleFieldChange('runtime', newValue)}
          onArchitectureChange={newValue => handleFieldChange('architecture', newValue)}
          runtimeLabel={labels.runtime || 'Runtime'}
          architectureLabel={labels.architecture || 'Architecture'}
          runtimeLabelId="lambda-runtime"
          architectureLabelId="lambda-architecture"
        />

        {showLambdaSource && (
          <Grid item xs={12} md={4}>
            <DefaultTextField
              label="Lambda Source"
              value={value.lambda_source ?? ''}
              onChange={newValue => handleFieldChange('lambda_source', newValue)}
              placeholder="e.g., solutions-api-lambdas"
            />
          </Grid>
        )}

        {showS3Key && (
          <Grid item xs={12} md={4}>
            <DefaultTextField
              label="S3 Key"
              value={value.s3_key ?? ''}
              onChange={newValue => handleFieldChange('s3_key', newValue)}
              placeholder="e.g., lambdas/my-function.zip"
            />
          </Grid>
        )}

        <MemoryTimeoutSnapstartFields
          memory={value.memory_size ?? DEFAULT_MEMORY}
          timeout={value.timeout ?? DEFAULT_TIMEOUT}
          snapstartEnabled={value.snapstart ?? defaultSnapstart}
          snapAliasEnabled={value.snap_alias ?? DEFAULT_SNAP_ALIAS_ENABLED}
          onMemoryChange={newValue => handleFieldChange('memory_size', newValue)}
          onTimeoutChange={newValue => handleFieldChange('timeout', newValue)}
          onSnapstartChange={checked =>
            // SnapStart requires the snap alias; turning it on forces snap_alias true.
            onChange(checked ? { snapstart: true, snap_alias: true } : { snapstart: false })
          }
          onSnapAliasChange={checked => handleFieldChange('snap_alias', checked)}
          memoryLabel={labels.memory || 'Memory (MB)'}
          timeoutLabel={labels.timeout || 'Timeout (seconds)'}
          snapstartDisabled={lmiEnabled}
          snapstartDisabledReason={LMI_INCOMPATIBLE_REASON}
          anchorName={anchorName}
        />

        {showCapacityProvider && (
          <CapacityProviderSelect
            value={(value as Lambda).capacity_provider ?? ''}
            onChange={newValue =>
              // LMI lambdas (capacity provider set) always create a snap alias.
              onChange(
                newValue
                  ? ({ capacity_provider: newValue, snap_alias: true } as Partial<Lambda>)
                  : ({ capacity_provider: newValue } as Partial<Lambda>),
              )
            }
            gridItem
          />
        )}
      </Grid>

      {/* Security Groups and Lambda Layers side by side */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 16 }}>
        <div style={{ flex: 1, minWidth: 300 }}>
          <SecurityGroupsSelector
            value={value.sg_ids || []}
            onChange={newValue => handleFieldChange('sg_ids', newValue)}
            label="Security Groups"
            helperText={
              lmiEnabled
                ? LMI_INCOMPATIBLE_REASON
                : 'Security groups for MongoDB private link access'
            }
            disabled={lmiEnabled}
          />
        </div>
        <div style={{ flex: 1, minWidth: 300 }}>
          <LambdaLayersSelector
            param_and_secrets_ext_enabled={value.param_and_secrets_ext_enabled ?? false}
            datadog_lambda_apm_enabled={value.datadog_lambda_apm_enabled ?? false}
            onChange={updates => onChange(updates)}
            label="Lambda Layers"
            helperText={lmiEnabled ? LMI_INCOMPATIBLE_REASON : 'Lambda layers to attach'}
            secretsDisabled={lmiEnabled}
            secretsDisabledReason={LMI_INCOMPATIBLE_REASON}
          />
        </div>
      </div>

      {/* Lambda Service Integrations — ARNs of other lambdas this lambda may invoke */}
      {showLambdaIntegrations && (
        <div style={{ marginTop: 16 }}>
          <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
            Lambda Service Integrations
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={(value as Lambda).lambda_integration_arns ?? []}
            onChange={(_e, newValue) =>
              handleFieldChange(
                'lambda_integration_arns' as keyof (Lambda | LambdaDefaults),
                newValue.filter(Boolean) as string[] as (Lambda | LambdaDefaults)[keyof (
                  | Lambda
                  | LambdaDefaults
                )],
              )
            }
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="arn:aws:lambda:${region}:${account_id}:function:my_lambda"
              />
            )}
          />
          <FormHelperText>
            ARNs of other lambdas this lambda may invoke. Terraform adds both the base ARN and the{' '}
            <code>:*</code> wildcard form for each entry.
          </FormHelperText>
        </div>
      )}

      {/* Environment Variables */}
      <LambdaEnvVarsEditor
        envVars={value.env_vars || {}}
        onUpdate={(envVarKey, envVarValue) => {
          const currentEnvVars = value.env_vars || {};
          const newEnvVars = { ...currentEnvVars };
          if (envVarValue.trim() === '') {
            delete newEnvVars[envVarKey];
          } else {
            newEnvVars[envVarKey] = envVarValue;
          }
          handleFieldChange('env_vars', newEnvVars);
        }}
        onAdd={() => {
          const newKey = `NEW_VAR_${Date.now()}`;
          const currentEnvVars = value.env_vars || {};
          handleFieldChange('env_vars', { ...currentEnvVars, [newKey]: '' });
        }}
        onDelete={envVarKey => {
          const currentEnvVars = value.env_vars || {};
          const newEnvVars = { ...currentEnvVars };
          delete newEnvVars[envVarKey];
          handleFieldChange('env_vars', newEnvVars);
        }}
        onRename={(oldKey, newKey) => {
          const currentEnvVars = value.env_vars || {};
          if (Object.prototype.hasOwnProperty.call(currentEnvVars, newKey) && newKey !== oldKey) {
            return; // Don't overwrite if new key already exists
          }
          const newEnvVars = { ...currentEnvVars };
          const envValue = currentEnvVars[oldKey];
          delete newEnvVars[oldKey];
          newEnvVars[newKey] = envValue;
          handleFieldChange('env_vars', newEnvVars);
        }}
        title={labels.envVars || 'Environment Variables'}
      />
    </>
  );
};
